// @ts-nocheck
import * as React from 'react';
import * as Scroll from 'react-scroll';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory, useLocation } from 'react-router-dom';
import { paginationConstants, TABLET_WIDTH } from 'commons/constants';
import { TABLET_SMALLER_WIDTH } from 'commons/constants/size';
import globalSearchActions from 'ducks/globalSearch/actions';
import Loader from 'components/uielements/Loader';
import MetaTags from 'components/MetaTags';
import SearchResultsList from 'components/SearchResultsList/index';
import SearchBarTopNav from 'components/SearchBarTopNav/index';
import { getTheme } from 'settings/theme';
import dotLoader from 'assets/images/dotLoader.gif';
import magnifyingGlassAnim from 'assets/images/magnifying-glass.json';
import noodleLogo from 'assets/images/noodle_logo.svg';
import type { PageType } from 'commons/types';
import Pagination from 'components/Pagination/index';
import SvgImageWrapper from 'components/SvgImage/SvgImage.style';
import { ReactComponent as ChevronLeftIcon } from 'assets/svg/chevron_left.svg';
import NooderaButton from 'components/NooderaButton';
import { useWidth } from 'utils/customHooks/index';
import getContentTypeDisplayName from 'utils/string/getContentTypeDisplayName';
import { getAppSettings, isNonNoodleInstance } from 'utils/privateLabel/index';
import type { GlobalSearchProps } from './GlobalSearchResultsList.types';
import GlobalSearchWrapper, {
  StyledSearch,
  Header,
  StyledLinkButton,
  StyledLottie,
  StyledParagraph,
  StyledTitle,
  StyledSubTitle,
} from './GlobalSearchResultsList.styles';

const { GLOBAL_SEARCH_PER_PAGE_WEB, GLOBAL_SEARCH_PER_PAGE_MOBILE } = paginationConstants;
const scroll = Scroll.animateScroll;
const { palette } = getTheme();

const GlobalSearchResultsList = ({ term }: GlobalSearchProps): React.ReactElement<any> => {
  const history = useHistory();
  const location = useLocation();
  const width = useWidth();
  const dispatch = useDispatch();
  const [search, setSearch] = React.useState('');
  const [activeFilter, setActiveFilter] = React.useState('');
  const [currentContentType, setCurrentContentType] = React.useState('');
  const [firstScreen, setFirstScreen] = React.useState();
  const searchResults = useSelector((state) => state.searchResults.searchResults);
  const counts = useSelector((state) => state.searchResults.counts);
  const isLoading = useSelector((state) => state.searchResults.isLoading);
  const currentTaxonomy = useSelector((state) => state.searchResults.currentTaxonomy);
  const currentPage = useSelector((state) => state.searchResults.currentPage);
  const { data: globalPartner } = useSelector((state) => state.partnerInstance.globalPartner);
  const totalCount: number = Object.keys(counts)?.length
    ? Object.values(counts).reduce((prevVal: any, currVal: any) => prevVal + currVal, 0)
    : 0;
  // Remove empty keys from search results
  const populatedSearchResults: Record<string, any> = Object.entries(searchResults).reduce(
    (acc, [k, v]: any) => (v?.length ? { ...acc, [k]: v } : acc),
    {},
  );

  const getPaginationLimit = (contentType: any): number => {
    if (!contentType) {
      return 5;
    }

    return width > TABLET_WIDTH ? GLOBAL_SEARCH_PER_PAGE_WEB : GLOBAL_SEARCH_PER_PAGE_MOBILE;
  };

  const globalSearchResultsGetter = (
    searchTerm: string,
    page: number,
    contentType?: string,
  ): void => {
    const elementsPerPage = getPaginationLimit(contentType);
    dispatch(
      globalSearchActions.getGlobalSearchRequest({
        term: searchTerm,
        page,
        elementsPerPage,
        contentType,
      }),
    );
  };

  React.useEffect(() => {
    const searchTerm = term || currentTaxonomy;

    if (searchTerm) {
      globalSearchResultsGetter(searchTerm, 1);
      setSearch(searchTerm);
    }

    return () => {
      dispatch(globalSearchActions.resetSearchResults());
    };
  }, []);

  const getBackButtonText = (): string => {
    const includesText = (text: string): boolean => location?.state?.prevPath?.includes(text);

    const text = 'Back to';
    const paths = {
      courses: 'Courses',
      home: '/',
      teams: 'Teams',
    };

    if (includesText(paths.courses.toLowerCase())) {
      return `${text} Programs`;
    }

    if (includesText(paths.teams.toLowerCase())) {
      return `${text} ${paths.teams}`;
    }

    if (includesText(paths.home.toLowerCase())) {
      return `${text} Home`;
    }

    return `Back`;
  };

  const onInputChange = (e: React.SyntheticEvent<HTMLInputElement>): void => {
    const {
      target: { value },
    } = e;
    setSearch(value);
  };

  const onPageChange = ({ selected }: PageType): void => {
    const page = selected + 1; // Add 1 to what we sent to the back end because it is 1-indexed

    if (currentPage !== page) {
      dispatch(globalSearchActions.setCurrentPage(page));
      globalSearchResultsGetter(search || term, page, currentContentType);
      scroll.scrollToTop({
        duration: 250,
      });
    }
  };

  const handleSearch = (): void => {
    setActiveFilter('');
    globalSearchResultsGetter(search, 1);
    setFirstScreen(false);
  };

  const handleSearchInput = (data: string): void => {
    history.push(`/search/${data}`);
  };

  const handleBackBtnClick = (): void => {
    if (currentTaxonomy) {
      history.replace(`/search`);
    } else {
      history.goBack();
    }
  };

  const handleCleanSearch = () => {
    setSearch('');
  };

  const handleClickFilterBtn = (contentType: string): void => {
    setActiveFilter(contentType);
    dispatch(globalSearchActions.setCurrentPage(1));
    globalSearchResultsGetter(search || term, 1, contentType);
    setCurrentContentType(contentType);
  };

  const handleClickAllFilterBtn = (): void => {
    setActiveFilter('');
    setCurrentContentType(currentContentType);
    globalSearchResultsGetter(search || term, 1, '');
  };

  const renderPagination = (): React.ReactElement<any> | null => {
    const paginationLimit = getPaginationLimit(activeFilter);

    if (!activeFilter) {
      return null;
    }

    return counts[activeFilter] > paginationLimit ? (
      <Pagination
        isLoading={isLoading}
        activePage={currentPage}
        count={Math.ceil(counts[activeFilter] / paginationLimit)}
        onPageChange={onPageChange}
      />
    ) : null;
  };

  const renderSearchBar = (): React.ReactElement<any> | null =>
    width < TABLET_SMALLER_WIDTH ? (
      <div className='global-search__bar'>
        <button
          className='global-search__back'
          onClick={handleBackBtnClick}
          onKeyPress={handleBackBtnClick}
          type='button'
        >
          <SvgImageWrapper color={palette.primary.ten}>
            <ChevronLeftIcon />
          </SvgImageWrapper>
        </button>
        <SearchBarTopNav
          autoFocus
          onInputChange={onInputChange}
          onSearch={handleSearch}
          search={search}
          toggled
          hideMobile={false}
          onCleanSearch={handleCleanSearch}
          placeholder='Search'
        />
      </div>
    ) : null;

  const renderSearchField = (): React.ReactElement<any> | null => {
    const paths = location.pathname.split('/');
    const searchField = paths[paths.length - 1];
    return width > TABLET_SMALLER_WIDTH ? (
      <StyledSearch
        withResults={!!totalCount}
        placeholder='Search for courses, schools, experts, and program pages'
        initValue={searchField === 'search' ? '' : searchField}
        onSearch={handleSearchInput}
      />
    ) : null;
  };

  const renderResults = (): React.ReactElement<any> => {
    if (isLoading) {
      return <Loader />;
    }

    if (!currentTaxonomy && !totalCount && !search) {
      return (
        <div className='global-search__search-something'>
          {width > TABLET_SMALLER_WIDTH ? (
            <>
              {renderSearchField()}
              <StyledLottie
                options={{
                  loop: false,
                  autoplay: true,
                  animationData: magnifyingGlassAnim,
                }}
                height={240}
                width={360}
              />
            </>
          ) : (
            <>
              <StyledLottie
                options={{
                  loop: false,
                  autoplay: true,
                  animationData: magnifyingGlassAnim,
                }}
                height={240}
                width={360}
              />
              <StyledParagraph>Search for schools, experts, and program pages.</StyledParagraph>
            </>
          )}
        </div>
      );
    }

    return totalCount ? (
      <div className='global-search__cards'>
        <SearchResultsList
          searchResults={populatedSearchResults}
          currentPage={currentPage}
          isLoading={isLoading}
          onClickViewAll={handleClickFilterBtn}
          activeFilter={activeFilter}
          counts={counts}
          totalCount={totalCount}
        />
      </div>
    ) : (
      <>
        {renderSearchField()}
        <div className='global-search__not-found'>
          <p className='global-search__not-found__title'>Sorry, no results found</p>
          <div className='global-search__not-found__icon-section'>
            <i
              className='icon-search sb__icon sb__search animated fadeIn'
              role='link'
              tabIndex='0'
            />
            <p>Check the spelling and try again</p>
          </div>
        </div>
      </>
    );
  };

  const renderHeader = (): React.ReactElement<any> => {
    const { companyLogo, companyName } = isNonNoodleInstance ? globalPartner : getAppSettings();
    const imgAltText = `${companyName} Logo`;
    return width > TABLET_SMALLER_WIDTH ? (
      <Header>
        <img
          src={isNonNoodleInstance ? companyLogo : noodleLogo}
          alt={imgAltText}
          aria-label={imgAltText}
          title={imgAltText}
          width='109'
          height='32'
          loading='lazy'
        />
      </Header>
    ) : (
      <></>
    );
  };

  const renderTitle = (): React.ReactElement<any> | null => {
    const hasResults = Object.values(counts).some((x) => x);
    const content = hasResults ? (
      <>
        {renderSearchField()}
        <StyledTitle>Results</StyledTitle>
        <StyledSubTitle>Filter Search</StyledSubTitle>
      </>
    ) : (
      <StyledTitle>Search</StyledTitle>
    );
    return hasResults || width > TABLET_SMALLER_WIDTH ? content : null;
  };

  const renderFilterBtns = (entries: any): React.ReactElement<any> | null =>
    entries.map((item) => {
      const title = getContentTypeDisplayName(item[0]);
      return item.length ? (
        <NooderaButton
          data-testid={`filter-${item[0]}-btn`}
          size='medium'
          key={title}
          isDisabled={activeFilter === item[0]}
          isActive={activeFilter === item[0]}
          onClick={() => {
            handleClickFilterBtn(item[0]);
          }}
        >
          {title}
        </NooderaButton>
      ) : null;
    });

  const renderFilterSection = (): React.ReactElement<any> | null => {
    const entries = Object.entries(counts);

    if (!currentContentType && isLoading) {
      return null;
    }

    return entries.length ? (
      <div className='global-search__filter'>
        <NooderaButton
          size='medium'
          isDisabled={!activeFilter}
          isActive={!activeFilter}
          onClick={handleClickAllFilterBtn}
        >
          All
        </NooderaButton>
        {renderFilterBtns(entries)}
      </div>
    ) : null;
  };

  const renderNumOfResults = (): React.ReactElement<any> | null => {
    if ((totalCount && !firstScreen) || term) {
      return null;
    }

    if (isLoading) {
      return (
        <img
          className='global-search__results-loader'
          src={dotLoader}
          alt='Loading...'
          loading='lazy'
          width='100%'
          height='100%'
        />
      );
    }

    if (!totalCount) {
      return null;
    }

    return <h2 className='global-search__results-title'>{totalCount} Results</h2>;
  };

  const renderBackBtn = (): React.ReactElement<any> | null =>
    width > TABLET_SMALLER_WIDTH ? (
      <StyledLinkButton
        onClick={handleBackBtnClick}
        isActive={false}
        className='global-search__back-btn'
        iconPosition='left'
      >
        {getBackButtonText()}
      </StyledLinkButton>
    ) : null;

  return (
    <>
      {renderHeader()}
      <MetaTags title={`${term || ''} | Search | ${getAppSettings()?.companyName}`} />
      <GlobalSearchWrapper>
        <div className='global-search__container'>
          {renderSearchBar()}
          <div className='global-search__inner-container'>
            {renderBackBtn()}
            {renderTitle()}
            {renderNumOfResults()}
            {renderFilterSection()}
            {renderResults()}
            {renderPagination()}
          </div>
        </div>
      </GlobalSearchWrapper>
    </>
  );
};

export default GlobalSearchResultsList;
